import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { ProgressBar } from "primereact/progressbar";
import { useContext, useState } from "react";
import {
  useDeleteLibraryScopeMutation,
  useGetLibrarySummaryQuery,
  useExportLibraryMutation,
  useImportLibraryMutation,
  usePreviewLibraryImportMutation,
} from "store/queries/payerProcedureServiceLines";
import { useGetAllPayersQuery } from "store/queries/payers";
import styled from "styled-components";
import {
  ILibraryImportStats,
  ILibrarySummaryEntry,
  LibraryClaimType,
} from "types/PayerProcedureServiceLine/PayerProcedureServiceLine";

import { LIBRARY_CLAIM_TYPE_OPTIONS } from "../utils/constants";
import { ToolFilePicker } from "./ToolFilePicker";

/**
 * CLE-2323 follow-on — Service Line Libraries admin sub-tab under Rate File Tools.
 *
 * Self-serve import of payer-supplied per-CPT service-line library
 * spreadsheets (Cigna, UHC, BCBS today; Aetna once Ani disambiguates the
 * Office/Outpatient suffix). Workflow:
 *
 *   1. Pick a payer + claim type.
 *   2. Upload the library file.
 *   3. Hit "Preview" — sees parsed-row count, distinct CPTs, and the
 *      distinct service-line labels that would be imported.
 *   4. Hit "Import" — wipes the (payer, claimType) slice and writes the new
 *      rows in a transaction.
 *
 * CLE-2323 r3: an earlier ProviderProcedure cross-reference filter was
 * removed per Ani — every CPT in the uploaded file is imported so the
 * multiplier is in place whenever a provider starts billing the code.
 *
 * Below the import workflow, a table lists existing libraries (one row per
 * payer + claim type) so the operator can see what's already imported and
 * delete an existing library if needed.
 *
 * CLE-2885: each library can also be downloaded. The original upload isn't
 * stored, so the file is rebuilt from the stored rows (CPT Code + Service
 * Line — re-importable as is). The server picks CSV or XLSX to match what
 * was uploaded; the file extension here follows the response's MIME type.
 */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ImportBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid var(--gray-300);
  border-radius: 6px;
  background: var(--gray-50);
`;

const Row = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  flex-wrap: wrap;

  > div {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  label {
    font-size: 0.85em;
    color: var(--gray-700);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

interface IPayerOption {
  label: string;
  value: number;
}

// Hoisted above the component to satisfy `@typescript-eslint/no-use-before-define`
// — function declarations would hoist at runtime, but the lint rule flags the
// before-defined call sites in the component body anyway.
function extractErrorMessage(e: unknown): string {
  const err = e as {
    data?: { message?: string; error?: string } | string;
    message?: string;
  };
  const data = err?.data;
  if (typeof data === "string") return data;
  return (
    data?.message ?? data?.error ?? err?.message ?? "An unknown error occurred."
  );
}

interface ILibraryDownloadButtonProps {
  entry: ILibrarySummaryEntry;
  payerName: string | undefined;
}

/**
 * CLE-2885 — one row's Download button. Each row owns its own export mutation,
 * so its spinner is its own and two rows can download at once. A mutation's
 * `unwrap()` hands back the response itself, so the file still saves if the row
 * unmounts mid-request (e.g. the operator switches sub-tab).
 */
function LibraryDownloadButton({
  entry,
  payerName,
}: ILibraryDownloadButtonProps) {
  const { current: toastElement } = useContext(ToastContext);
  const [exportLibrary, { isLoading }] = useExportLibraryMutation();

  const download = async () => {
    try {
      const { blob } = await exportLibrary({
        payerId: entry.payerId,
        claimType: entry.claimType,
      }).unwrap();
      const ext = blob.type.startsWith("text/csv") ? "csv" : "xlsx";
      const payerPart = payerName ?? `payer-${entry.payerId}`;
      const claimPart = entry.claimType.toLowerCase();
      const fileName = `${payerPart}-${claimPart}-service-line-library.${ext}`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      // Anything but letters, digits, `.` and `_` becomes one `-` (payer names carry
      // spaces, slashes and parentheses), and no leading `-`.
      link.download = fileName
        .replace(/[^A-Za-z0-9._]+/g, "-")
        .replace(/^-+/, "");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      toastElement?.show({
        severity: "error",
        summary: "Download failed",
        detail: extractErrorMessage(e),
      });
    }
  };

  return (
    <Button
      icon="pi pi-download"
      className="p-button-sm p-button-text"
      onClick={download}
      loading={isLoading}
      disabled={isLoading}
      aria-label="Download"
    />
  );
}

export function ServiceLineLibraries() {
  const { current: toastElement } = useContext(ToastContext);
  const { data: payersData } = useGetAllPayersQuery();
  const { data: summaryData, isFetching: isLoadingSummary } =
    useGetLibrarySummaryQuery();

  const [file, setFile] = useState<File | null>(null);
  const [payerId, setPayerId] = useState<number | null>(null);
  const [claimType, setClaimType] = useState<LibraryClaimType>("PROFESSIONAL");
  const [preview, setPreview] = useState<ILibraryImportStats | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [previewImport, { isLoading: isPreviewing }] =
    usePreviewLibraryImportMutation();
  const [doImport, { isLoading: isImporting }] = useImportLibraryMutation();
  const [deleteScope] = useDeleteLibraryScopeMutation();

  const busy = isPreviewing || isImporting;

  const payerOptions: IPayerOption[] = (payersData?.data ?? []).map(
    (p: any) => ({
      label: p.name,
      value: p.id,
    })
  );
  const payerNameById = new Map<number, string>(
    payerOptions.map((p) => [p.value, p.label])
  );

  const canSubmit = !!file && payerId != null && !busy;

  const clearForm = () => {
    setFile(null);
    setPreview(null);
  };

  /** Choosing or removing a file: the preview and error described the previous one. */
  const onFileChange = (next: File | null) => {
    setFile(next);
    setPreview(null);
    setErrorMessage(null);
  };

  const runPreview = async () => {
    if (!file || payerId == null) return;
    setErrorMessage(null);
    setPreview(null);
    try {
      const result = await previewImport({ file, payerId, claimType }).unwrap();
      setPreview(result.data);
    } catch (e: any) {
      setErrorMessage(extractErrorMessage(e));
    }
  };

  const runImport = async () => {
    if (!file || payerId == null) return;
    setErrorMessage(null);
    try {
      const result = await doImport({ file, payerId, claimType }).unwrap();
      setPreview(result.data);
      toastElement?.show({
        severity: "success",
        summary: "Library imported",
        detail: `Inserted ${result.data.insertedRows.toLocaleString()} rows for ${
          payerNameById.get(payerId) ?? `payer ${payerId}`
        } (${claimType.toLowerCase()}).`,
      });
      clearForm();
    } catch (e: any) {
      setErrorMessage(extractErrorMessage(e));
    }
  };

  const onDelete = (entry: ILibrarySummaryEntry) => {
    confirmDialog({
      header: "Delete library",
      message: `Delete the ${entry.claimType.toLowerCase()} library for ${
        payerNameById.get(entry.payerId) ?? `payer ${entry.payerId}`
      }? This removes ${entry.rowCount.toLocaleString()} rows. You can re-import the file later.`,
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await deleteScope({
            payerId: entry.payerId,
            claimType: entry.claimType,
          }).unwrap();
          toastElement?.show({
            severity: "success",
            summary: "Deleted",
            detail: "Library removed.",
          });
        } catch (e: any) {
          toastElement?.show({
            severity: "error",
            summary: "Delete failed",
            detail: extractErrorMessage(e),
          });
        }
      },
    });
  };

  // Extract DataTable body renderers to stable references (react/no-unstable-nested-components).
  const payerCellBody = (entry: ILibrarySummaryEntry) =>
    payerNameById.get(entry.payerId) ?? `Payer ${entry.payerId}`;

  const updatedCellBody = (entry: ILibrarySummaryEntry) =>
    entry.latestUpdatedAt
      ? new Date(entry.latestUpdatedAt).toLocaleString()
      : "—";

  const actionsCellBody = (entry: ILibrarySummaryEntry) => (
    <div className="flex align-items-center">
      <LibraryDownloadButton
        entry={entry}
        payerName={payerNameById.get(entry.payerId)}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-sm p-button-text p-button-danger"
        onClick={() => onDelete(entry)}
        aria-label="Delete"
      />
    </div>
  );

  return (
    <Wrapper>
      <div>
        <h3 style={{ marginBottom: "0.25rem" }}>Service Line Libraries</h3>
        <p style={{ margin: 0, color: "var(--gray-600)" }}>
          Per-payer CPT &rarr; service-line mapping. Imported from each
          payer&apos;s &quot;service line library&quot; spreadsheet (e.g. Cigna
          provides one with columns <code>Procedure Code</code> +{" "}
          <code>Subspecialty</code>). Read by the Rate Expander as a fallback
          when the base rate file doesn&apos;t carry a Service Line column.
          Range-based files (Aetna) are not yet supported &mdash; convert to one
          row per CPT first. Libraries are global per payer.
        </p>
      </div>

      <ImportBox>
        <h4 style={{ margin: 0 }}>Import a library file</h4>
        <Row>
          <div style={{ minWidth: 240 }}>
            <label htmlFor="lib-payer">Payer</label>
            <Dropdown
              inputId="lib-payer"
              value={payerId}
              options={payerOptions}
              onChange={(e) => setPayerId(e.value ?? null)}
              placeholder="Select payer"
              filter
              disabled={busy}
            />
          </div>
          <div style={{ minWidth: 200 }}>
            <label htmlFor="lib-claim-type">Claim type</label>
            <Dropdown
              inputId="lib-claim-type"
              value={claimType}
              options={[...LIBRARY_CLAIM_TYPE_OPTIONS]}
              onChange={(e) => setClaimType(e.value)}
              disabled={busy}
            />
          </div>
          <div>
            <label>Library file (CSV or XLSX)</label>
            <ToolFilePicker
              file={file}
              onChange={onFileChange}
              chooseLabel="Choose file"
              disabled={busy}
            />
          </div>
        </Row>

        <ButtonRow>
          <Button
            label="Preview"
            icon="pi pi-eye"
            className="p-button-outlined"
            onClick={runPreview}
            disabled={!canSubmit}
            loading={isPreviewing}
          />
          <Button
            label="Import (replaces existing)"
            icon="pi pi-upload"
            onClick={runImport}
            disabled={!canSubmit}
            loading={isImporting}
          />
        </ButtonRow>

        {busy && <ProgressBar mode="indeterminate" style={{ height: "6px" }} />}

        {errorMessage && (
          <Message
            severity="error"
            text={errorMessage}
            style={{ alignItems: "flex-start" }}
          />
        )}

        {preview && !errorMessage && (
          <Message
            severity={preview.imported ? "success" : "info"}
            content={
              <div>
                <div>
                  <strong>
                    {preview.imported
                      ? `Imported ${preview.insertedRows.toLocaleString()} rows.`
                      : "Preview"}
                  </strong>
                </div>
                <div>
                  Detected column headers:{" "}
                  <code>{preview.parseDiagnostics.cptColumnHeader}</code> /{" "}
                  <code>
                    {preview.parseDiagnostics.serviceLineColumnHeader}
                  </code>
                </div>
                <div>
                  Parsed {preview.parsedRows.toLocaleString()} rows &rarr;{" "}
                  {preview.distinctCpts.toLocaleString()} distinct CPTs ready to
                  import.
                </div>
                <div>
                  {preview.distinctServiceLines.length.toLocaleString()}{" "}
                  distinct service-line labels would be imported.
                </div>
                {(preview.parseDiagnostics.droppedBlankRows > 0 ||
                  preview.parseDiagnostics.droppedInvalidCpt > 0 ||
                  preview.parseDiagnostics.droppedEmptyServiceLine > 0) && (
                  <div style={{ color: "var(--gray-700)", fontSize: "0.9em" }}>
                    Skipped during parse:{" "}
                    {preview.parseDiagnostics.droppedBlankRows} blank rows,{" "}
                    {preview.parseDiagnostics.droppedInvalidCpt} invalid CPTs,{" "}
                    {preview.parseDiagnostics.droppedEmptyServiceLine} rows with
                    empty service line
                  </div>
                )}
                {preview.distinctServiceLines.length > 0 &&
                  preview.distinctServiceLines.length <= 50 && (
                    <details style={{ marginTop: "0.5rem" }}>
                      <summary>
                        Show all{" "}
                        {preview.distinctServiceLines.length.toLocaleString()}{" "}
                        service-line labels
                      </summary>
                      <ul style={{ margin: "0.5rem 0 0 1.2rem", padding: 0 }}>
                        {preview.distinctServiceLines.map((sl) => (
                          <li key={sl}>
                            <code>{sl}</code>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
              </div>
            }
            style={{ alignItems: "flex-start" }}
          />
        )}
      </ImportBox>

      <div>
        <h4 style={{ marginBottom: "0.5rem" }}>Imported libraries</h4>
        <DataTable
          value={summaryData?.data ?? []}
          emptyMessage="No libraries imported yet."
          loading={isLoadingSummary}
          size="small"
        >
          <Column
            header="Payer"
            body={payerCellBody}
            field="payerId"
            sortable
          />
          <Column field="claimType" header="Claim type" sortable />
          <Column field="rowCount" header="Rows" sortable />
          <Column
            field="distinctServiceLines"
            header="Distinct service lines"
            sortable
          />
          <Column
            field="latestSource"
            header="Source"
            body={(e: ILibrarySummaryEntry) => e.latestSource ?? "—"}
          />
          <Column header="Updated" body={updatedCellBody} />
          <Column header="Actions" body={actionsCellBody} />
        </DataTable>
      </div>

      <ConfirmDialog />
    </Wrapper>
  );
}

export default ServiceLineLibraries;
